"use client";

import { Badge } from "@tracify/ui/badge";
import type { ChatMessage } from "@tracify/shared";
import type {
  KpiBreakdownData,
  KpiBreakdownForChannelResponse,
} from "@tracify/shared";

type ToolPart = Extract<
  ChatMessage["parts"][number],
  { type: `tool-${string}` }
>;

function extractBreakdownLabel(
  row: KpiBreakdownData,
  dimension: KpiBreakdownForChannelResponse["breakdown_dimension"]
) {
  const info = row.info;

  if (dimension === "campaign") return info.campaign_name ?? info.campaign_id;
  if (dimension === "adset") return info.adset_name ?? info.adset_id;
  if (dimension === "ad") return info.ad_name ?? info.ad_id;
  if (dimension === "creative") return info.ad_creative_id ?? info.ad_name;
  if (dimension === "advertising_channel_type") {
    return (
      info.advertising_channel_type ?? info.campaign_name ?? info.campaign_id
    );
  }

  return info.campaign_name ?? info.ad_name ?? info.ad_id;
}

function renderKpiBreakdown(output: unknown) {
  const data = output as KpiBreakdownForChannelResponse;
  const dimension = data?.breakdown_dimension;
  const rows = data?.breakdown?.data ?? [];
  const total = data?.breakdown?.pagination?.total ?? rows.length;

  if (!data?.channel || !dimension) {
    return null;
  }

  const topLabels = rows
    .slice(0, 3)
    .map((row) => extractBreakdownLabel(row, dimension))
    .filter((label): label is string => Boolean(label));

  return (
    <div className="flex flex-col gap-2 text-sm">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="sky">{data.channel}</Badge>
        <Badge variant="indigo">{dimension}</Badge>
        <span>{total.toLocaleString()} rows</span>
      </div>

      {topLabels.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {topLabels.map((label) => (
            <Badge key={label} variant="sky" className="font-normal">
              {label}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export function ToolCallOutput({ part }: { part: ToolPart }) {
  if (part.state !== "output-available") {
    return null;
  }

  if (part.type === "tool-getChannelKpiBreakdown") {
    return renderKpiBreakdown(part.output);
  }

  return null;
}
